<template>
  <div>
    <LogInOptionsDialog
      :closeAuth="closeAuth"
      :outsideClick="outsideClick"
      :show="activeDialog === $options.Views.options"
      :toAuthRoute="toAuthRoute"
      :toLogInRoute="toLogInRoute"
    />
    <LogInEmailDialog
      :closeAuth="closeAuth"
      :email="email"
      :logInWithEmailAndPassword="logInWithEmailAndPassword"
      :outsideClick="outsideClick"
      :password="password"
      :show="activeDialog === $options.Views.email"
      :toAuthRoute="toAuthRoute"
      :toLogInRoute="toLogInRoute"
      :update="update"
    />
    <LogInPhoneDialog
      :closeAuth="closeAuth"
      :outsideClick="outsideClick"
      :phone="phone"
      :show="activeDialog === $options.Views.phone"
      :toAuthRoute="toAuthRoute"
      :toLogInRoute="toLogInRoute"
      :updatePhone="updatePhone"
    />
    <LogInSMSDialog
      :closeAuth="closeAuth"
      :code="code"
      :fetchAccountStatus="fetchAccountStatus"
      :outsideClick="outsideClick"
      :phone="phone"
      :show="activeDialog === $options.Views.sms"
      :toAuthRoute="toAuthRoute"
      :toLogInRoute="toLogInRoute"
      :updateCode="updateCode"
    />
  </div>
</template>

<script>
import { Views } from "@/types/login.js";
import { mapGetters, mapState } from "vuex";
import { formatPhoneNumber } from "@/util.js";
import LogInOptionsDialog from "@/components/Auth/LogIn/Dialogs/Options.vue";
import LogInEmailDialog from "@/components/Auth/LogIn/Dialogs/Email.vue";
import LogInPhoneDialog from "@/components/Auth/LogIn/Dialogs/Phone.vue";
import LogInSMSDialog from "@/components/Auth/LogIn/Dialogs/SMS.vue";

export default {
  name: "LogIn",
  components: {
    LogInOptionsDialog,
    LogInEmailDialog,
    LogInPhoneDialog,
    LogInSMSDialog,
  },
  computed: {
    ...mapGetters({
      authorized: "auth/isAuthenticated",
      userClickedCartButtonBeforeAuthenticated:
        "cart/userClickedCartButtonBeforeAuthenticated",
    }),
    ...mapState({
      account: (state) => state.auth.account,
      rerouteOrderId: (state) => state.orders.rerouteOrderId,
      showSMSRetryMessage: (state) => state.auth.showSMSRetryMessage,
      user: (state) => state.user.info,
      websiteFormat: (state) =>
        state.store.info.store_info.customization?.website.style
          .website_format ?? "all_items",
    }),
  },
  data() {
    return {
      activeDialog: Views.options,
      code: { input0: null, input1: null, input2: null, input3: null },
      email: null,
      password: null,
      phone: null,
    };
  },
  methods: {
    closeDialogAfterLogin() {
      // order important here: closeAuth() will reset userClickedCartButtonBeforeAuthenticated
      this.userClickedCartButtonBeforeAuthenticated &&
        this.$store.commit("cart/SET", true);
      this.closeAuth();
    },
    async fetch() {
      // don't await
      this.$store.dispatch("orders/fetch");
      this.$store.dispatch("favorites/fetch");

      // await to open cart if products after click on cart and login etc.
      await this.$store.dispatch("cart/fetch");
      this.closeDialogAfterLogin();
      this.routeToAccount();
    },
    async fetchAccountStatus() {
      this.$store.commit("auth/SHOW_SMS_RETRY_MESSAGE", false);
      const { input0, input1, input2, input3 } = this.code;
      const verificationCode = input0.concat(input1, input2, input3);

      const info = {
        country_code: 1,
        phone: this.phone,
        verification_code: verificationCode,
      };
      await this.$store.dispatch("auth/fetchAccountStatus", info);
      if (this.showSMSRetryMessage) {
        return;
      }

      this.authorized && !this.account
        ? this.fetch()
        : this.toAuthRoute("signup", true); // true is to determine if fromLogin
    },
    async logInWithEmailAndPassword() {
      const info = {
        email: this.email,
        password: this.password,
      };
      await this.$store.dispatch("auth/login", info);
      if (!this.authorized) {
        return;
      }
      this.user.phone ? this.fetch() : this.toAuthRoute("signup");
    },
    routeToAccount() {
      let hash, params;
      if (this.websiteFormat === "store_info_rewards") {
        hash = "#rewards";
      }
      if (this.rerouteOrderId) {
        hash = "#orders";
        params = { orderId: this.rerouteOrderId };
      }
      if (hash) {
        this.$router.push({
          name: "account",
          hash: hash,
          params: params,
        });
      }
    },
    toLogInRoute(dialog) {
      this.activeDialog = dialog;
    },
    update(name, val) {
      this[name] = val;
    },
    updateCode(input, val) {
      this.code[`input${input}`] = val;
    },
    updatePhone(val) {
      this.phone = formatPhoneNumber(val);
    },
  },
  mounted() {
    this.$store.commit("auth/SHOW_SMS_RETRY_MESSAGE", false);
  },
  props: {
    closeAuth: Function,
    outsideClick: Function,
    toAuthRoute: Function,
  },
  Views,
};
</script>
